import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  ArrowLeft,
  UserPlus,
  Loader2,
} from "lucide-react";
import api from "@/services/api";
import toast from "react-hot-toast";

interface Manager {
  id: number;
  firstName: string;
  lastName: string;
  employeeNumber: string;
}

interface EmployeeFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  department: string;
  position: string;
  employmentType: "full_time" | "part_time" | "contract";
  baseSalary: string;
  salaryCurrency: "USD" | "CDF";
  hireDate: string;
  managerId?: number;
  bankName: string;
  bankAccount: string;
  transportAllowance: string;
  telephoneAllowance: string;
  dataAllowance: string;
}

const AddEmployee: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<EmployeeFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    department: "",
    position: "",
    employmentType: "full_time",
    baseSalary: "",
    salaryCurrency: "USD",
    hireDate: new Date().toISOString().split("T")[0],
    bankName: "",
    bankAccount: "",
    transportAllowance: "0",
    telephoneAllowance: "0",
    dataAllowance: "0",
  });



  const { data: managers } = useQuery<{ employees: Manager[] }>({
    queryKey: ["potential-managers"],
    queryFn: async () => {
      const response = await api.get("/employees?limit=100");
      return response.data.data;
    },
  });

  const addEmployeeMutation = useMutation({
    mutationFn: async (data: EmployeeFormData) => {
      const response = await api.post("/employees", data);
      return response.data;
    },
    onSuccess: () => {
      toast.success("Employee added successfully");
      navigate("/employees");
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.error?.message || "Failed to add employee"
      );
    },
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "managerId" ? (value ? parseInt(value) : undefined) : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addEmployeeMutation.mutate(formData);
  };

  const departments = [
    "Human Resources",
    "Information Technology",
    "Finance",
    "Sales",
    "Marketing",
    "Operations",
    "Legal",
    "Administration",
  ];

  const getCurrencySymbol = () =>
    formData.salaryCurrency === "USD" ? "$" : "FC";

  const inputClasses = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-sm";
  const selectClasses = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm";

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-indigo-600 rounded-lg flex items-center justify-center">
            <UserPlus size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Add New Employee</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Create a new employee record in the system
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate("/employees")}
          className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <ArrowLeft size={14} /> Back to Employees
        </button>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-6 space-y-6"
      >
        {/* Personal Information */}
        <div className="bg-gray-50/50 dark:bg-gray-700/50 rounded-lg p-5 border border-gray-200 dark:border-gray-600">
          <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <div className="w-1 h-4 bg-indigo-600 rounded-full mr-2"></div>
            Personal Information
          </h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
                First Name *
              </label>
              <input
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                className={inputClasses}
                placeholder="Enter first name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
                Last Name *
              </label>
              <input
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                className={inputClasses}
                placeholder="Enter last name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={inputClasses}
                placeholder="employee@company.com"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+243 81 234 5678"
                className={inputClasses}
              />
            </div>
          </div>
        </div>

        {/* Job Information */}
        <div className="bg-gray-50/50 dark:bg-gray-700/50 rounded-lg p-5 border border-gray-200 dark:border-gray-600">
          <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <div className="w-1 h-4 bg-indigo-600 rounded-full mr-2"></div>
            Job Information
          </h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
                Department *
              </label>
              <select
                name="department"
                required
                value={formData.department}
                onChange={handleChange}
                className={selectClasses}
              >
                <option value="">Select Department</option>
                {departments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
                Position *
              </label>
              <input
                name="position"
                required
                value={formData.position}
                onChange={handleChange}
                placeholder="e.g., Software Engineer"
                className={inputClasses}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
                Employment Type *
              </label>
              <select
                name="employmentType"
                required
                value={formData.employmentType}
                onChange={handleChange}
                className={selectClasses}
              >
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="contract">Contract</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
                Hire Date *
              </label>
              <input
                type="date"
                name="hireDate"
                required
                value={formData.hireDate}
                onChange={handleChange}
                className={inputClasses}
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
              Reports To (Manager)
            </label>
            <select
              name="managerId"
              value={formData.managerId || ""}
              onChange={handleChange}
              className={selectClasses}
            >
              <option value="">No Manager</option>
              {managers?.employees?.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.firstName} {m.lastName} (#{m.employeeNumber})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Compensation */}
        <div className="bg-gray-50/50 dark:bg-gray-700/50 rounded-lg p-5 border border-gray-200 dark:border-gray-600">
          <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <div className="w-1 h-4 bg-indigo-600 rounded-full mr-2"></div>
            Compensation & Benefits
          </h4>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
                Base Salary *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 text-xs">
                  {getCurrencySymbol()}
                </span>
                <input
                  type="number"
                  name="baseSalary"
                  required
                  min="0"
                  step="0.01"
                  value={formData.baseSalary}
                  onChange={handleChange}
                  placeholder="0.00"
                  className={`${inputClasses} pl-10`}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
                Currency *
              </label>
              <select
                name="salaryCurrency"
                required
                value={formData.salaryCurrency}
                onChange={handleChange}
                className={selectClasses}
              >
                <option value="USD">USD ($)</option>
                <option value="CDF">CDF (FC)</option>
              </select>
            </div>
          </div>

          <h5 className="text-xs font-semibold text-gray-900 dark:text-white mb-3">
            Monthly Allowances
          </h5>
          <div className="grid md:grid-cols-3 gap-3">
            {["transportAllowance", "telephoneAllowance", "dataAllowance"].map(
              (key) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2 capitalize">
                    {key.replace("Allowance", "")}
                  </label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 text-xs">
                      {getCurrencySymbol()}
                    </span>
                    <input
                      type="number"
                      name={key}
                      min="0"
                      step="0.01"
                      value={(formData as any)[key]}
                      onChange={handleChange}
                      placeholder="0.00"
                      className={`${inputClasses} pl-8`}
                    />
                  </div>
                </div>
              )
            )}
          </div>
        </div>

        {/* Bank Details */}
        <div className="bg-gray-50/50 dark:bg-gray-700/50 rounded-lg p-5 border border-gray-200 dark:border-gray-600">
          <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <div className="w-1 h-4 bg-indigo-600 rounded-full mr-2"></div>
            Bank Details
          </h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
                Bank Name
              </label>
              <input
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
                placeholder="e.g., Equity BCDC"
                className={inputClasses}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
                Account Number
              </label>
              <input
                name="bankAccount"
                value={formData.bankAccount}
                onChange={handleChange}
                placeholder="e.g., 1234567890"
                className={inputClasses}
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end border-t border-gray-200 dark:border-gray-600 pt-4">
          <button
            type="button"
            onClick={() => navigate("/employees")}
            className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors mr-3"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={addEmployeeMutation.isPending}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center disabled:opacity-50"
          >
            {addEmployeeMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" /> Add Employee
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddEmployee;